<template>
  <div>
    <div class="text-start">
      <h3 class="fw-bold">{{ title }}</h3>
    </div>
    <div class="card card-body shadow-sm text-start p-2 rounded-5">
      <table
        class="table text-start table-borderless m-0 align-middle rounded-5"
      >
        <tbody v-if="mappedAchievements.length > 0">
          <tr
            v-for="(item, index) in mappedAchievements"
            :key="item"
            :class="[`${item.color}-leaderboard`, fontSizeByIndex(index)]"
          >
            <td class="text-center">
              <div class="badge rounded-circle bg-light text-dark">
                {{ index + 1 }}
              </div>
            </td>
            <!-- ICON -->
            <td>
              <div class="d-flex align-items-center justify-content-center">
                <GroupIcon
                  :index="item.index"
                  :width="iconWidthByIndex(index)"
                  v-if="
                    selectedLeaderboardFilter === LEADERBOARD_FILTERS.GROUPS
                  "
                />
                <UserProfile
                  v-else
                  :fullname="item.fullname"
                  :userImage="item.userImage"
                  :size="iconWidthByIndex(index)"
                />
              </div>
            </td>
            <!-- NAME -->
            <td>
              <div class="d-flex align-items-center">
                <span class="ms-1">{{ item.name }}</span>
              </div>
            </td>
            <!-- POINTS -->
            <td>
              <span>{{ item.points }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/* https://vuejs.org/guide/essentials/lifecycle.html#lifecycle-diagram */

import { mapGetters } from "vuex";

// Helper functions
import { getFinalCompletionTimestamp, shortenName } from "@/helpers/common.js";

// Components
import UserProfile from "@/components/activity/UserProfile.vue";
import GroupIcon from "@/components/activity/GroupIcon.vue";
// Data
import { LEADERBOARD_FILTERS, TIME_FILTERS } from "@/data/enums.js";

export default {
  name: "LeaderboardCard",
  components: {
    UserProfile,
    GroupIcon
  },
  data() {
    return {
      LEADERBOARD_FILTERS
    };
  },
  props: {
    title: {
      type: String,
      required: true
    },
    selectedTimeFilter: {
      type: String,
      required: true
    },
    selectedLeaderboardFilter: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters("groups", ["getMappedGroups"]),
    mappedUsers() {
      let groups = this.getMappedGroups;

      groups = groups.map((group) => {
        let members = group.members.filter((member) => member.fullname);
        return members.map((member) => {
          return {
            ...member,
            points:
              this.selectedTimeFilter === TIME_FILTERS.WEEKLY
                ? member.pointsWeekly
                : member.pointsAllTime,
            color: group.color,
            name: shortenName(member.fullname)
          };
        });
      });

      return groups.flat();
    },
    mappedAchievements() {
      // Individual Filter
      if (this.selectedLeaderboardFilter === LEADERBOARD_FILTERS.INDIVIDUAL) {
        let users = this.rankedIndividualLeaderboard(this.mappedUsers);

        users = users.slice(0, 4);

        return users;
      }
      // Groups Filter
      else if (this.selectedLeaderboardFilter === LEADERBOARD_FILTERS.GROUPS) {
        let groups = this.getMappedGroups;

        groups = this.rankedGroupLeaderboard(groups);
        return groups;
      } else {
        return [];
      }
    }
  },
  methods: {
    // Sets up leaderboard for individual users
    rankedIndividualLeaderboard() {
      let users = this.mappedUsers;

      // Sort users from highest to lowest points, break ties by earliest full score completion
      users.sort((a, b) => {
        if (b.points !== a.points) {
          return b.points - a.points;
        }

        const aTime = getFinalCompletionTimestamp(a.codes);
        const bTime = getFinalCompletionTimestamp(b.codes);

        return aTime - bTime;
      });

      return users;
    },
    // Sets up leaderboard for groups
    rankedGroupLeaderboard(entries) {
      let groups = entries;

      groups = groups.map((group) => {
        // Group's completion time is the latest among users who earned points
        const groupCompletionTime = group.members
          .filter((member) =>
            this.selectedTimeFilter === TIME_FILTERS.WEEKLY
              ? member.pointsWeekly > 0
              : member.pointsAllTime > 0
          )
          .reduce((latest, member) => {
            return Math.max(latest, member.completionTime);
          }, 0);

        return {
          ...group,
          points:
            this.selectedTimeFilter === TIME_FILTERS.WEEKLY
              ? group.pointsWeekly
              : group.pointsAllTime,
          completionTime: groupCompletionTime
        };
      });

      // Sort groups by total points desc, then by earliest completion time
      groups.sort((a, b) => {
        if (b.points !== a.points) {
          return b.points - a.points;
        }

        return a.completionTime - b.completionTime;
      });

      return groups;
    },
    fontSizeByIndex(index) {
      if (index === 0) {
        return "fs-2";
      } else if (index === 1) {
        return "fs-3";
      } else if (index === 2) {
        return "fs-4";
      } else {
        return "fs-6";
      }
    },
    // Determine icon width based on index
    // This is to make first place larger than second, etc.
    iconWidthByIndex(index) {
      if (index === 0) {
        return 60;
      } else if (index === 1) {
        return 50;
      } else if (index === 2) {
        return 45;
      } else {
        return 40;
      }
    }
  }
};
</script>

<style scoped>
tr:first-child td:first-child {
  border-top-left-radius: 1.5rem !important; /* same as .rounded's default radius */
}

tr:first-child td:last-child {
  border-top-right-radius: 1.5rem !important; /* same as .rounded's default radius */
}

tr:last-child td:first-child {
  border-bottom-left-radius: 1.5rem !important; /* same as .rounded's default radius */
}

tr:last-child td:last-child {
  border-bottom-right-radius: 1.5rem !important; /* same as .rounded's default radius */
}

tr:not(:last-child) {
  border-bottom: 1px solid white;
}
</style>
